import {Component, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import {User, UserPlatformRole} from 'src/app/shared/models/user';
import {ApiService} from '../../shared/services/api.service';
import {FilterWithType, LangService, LoaderService, NavigateService, SortOrderEnum, TableComponent, TableConfig, TableDataTransformer, TopButton} from 'ngx-satoris';
import {ORIGINAL_TABLE_USERS_CONFIG, TABLE_USERS_CONFIG, TableUsersEnum, TableUsersFilterEnum} from './table-config';
import {PaginationQuery, SortQuery} from 'ngx-satoris/lib/shared/models/params';
import {environment} from 'src/environments/environment';

@Component({
  selector: 'app-users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class UsersComponent implements OnInit {
  data: User[];
  defaultPaginatorSize = 10;
  totalCount: number;
  config: TableConfig = {...TABLE_USERS_CONFIG};
  readonly initialConfig: TableConfig = {...ORIGINAL_TABLE_USERS_CONFIG};

  tableDataTransformer: TableDataTransformer<User>;
  topButtons: TopButton[] = [{label: this.lang.transform('create_user.title'), icon: 'add', action: () => this.nav.to('user-create')}];

  constructor(public nav: NavigateService,
    public api: ApiService,
    private loader: LoaderService,
    private lang: LangService) {
  }

  @ViewChild('smartTable') smartTable: TableComponent;

  ngOnInit() {
    this.setupTableDataTransformer();
  }

  setupTableDataTransformer(): void {
    this.tableDataTransformer = {
      transformData: (userData: User) => ({
        [TableUsersEnum.ID]: {value: userData.id || ''},
        [TableUsersEnum.USER_ID]: {value: userData.userId || ''},
        [TableUsersEnum.NAME]: {value: userData.name || ''},
        [TableUsersEnum.ROLE]: {value: this.lang.transform(`DYN.role.${userData.role}`) || userData.role || ''},
        [TableUsersEnum.EMAIL]: {value: userData.accountName || ''},
        [TableUsersEnum.CREATION_DATE]: {value: userData.confirmedAt || ''}
      })
    };
    const tableConfig = JSON.parse(JSON.stringify(TABLE_USERS_CONFIG));
    tableConfig.dataTransformer = this.tableDataTransformer;
    tableConfig.storagePrefix = this.api.userInfo.id+'';
    this.config = tableConfig;
  }

  /**
   * Fetches the list of users with optional pagination and sorting.
   * @param pagination Optional pagination parameters.
   * @param sort Optional sorting parameters.
   * @returns A promise that resolves to an array of users.
   */
  getUsers(pagination?: PaginationQuery, filters?: FilterWithType[], sort?: SortQuery): Promise<User[]> {
    let sortOrder: 'ASC' | 'DESC' = null;
    let sortColumn: 'id' | 'name' | 'role' | 'accountName' = null;
    if(sort?.order && sort?.column?.value) {
      sortOrder = sort.order === SortOrderEnum.DESC ? 'DESC' : 'ASC';
      sortColumn = sort.column.value as 'id' | 'name' | 'role' | 'accountName';
    }
    if(sort?.order === 'none') {
      sortOrder = sortColumn = null;
    }
    let userId: number;
    let name: string;
    let role: string;
    let email: string;
    let creationDateStart: number;
    let creationDateEnd: number;

    filters?.forEach(filter => {
      if(filter.serverFilter && filter.value) {
        switch(filter.filterId) {
        case TableUsersFilterEnum.USER_ID:
          userId = filter.value as number;
          break;
        case TableUsersFilterEnum.NAME:
          name = filter.value as string;
          break;
        case TableUsersFilterEnum.ROLE:
          role = filter.value as string;
          break;
        case TableUsersFilterEnum.EMAIL:
          email = filter.value as string;
          break;
        case TableUsersFilterEnum.CREATE_AT_START:
          creationDateStart = new Date(filter.value as number).getTime();
          break;
        case TableUsersFilterEnum.CREATE_AT_END:
          creationDateEnd = new Date(new Date(filter.value as number).setHours(23, 59, 59, 999)).getTime();
          break;
        }
      }
    });
    return this.api.users(true, userId, name, role, email, creationDateStart, creationDateEnd, sortColumn, sortOrder, pagination?.offset, pagination?.limit).then((res) => {
      this.data = environment.hideNoctisConsumerUsersInUsersTable
        ? res.result.filter(user => user.role !== UserPlatformRole.ORBIS_CONSUMER)
        : res.result;
      this.totalCount = +res.count;

      return this.data;
    }).catch((err) => {
      throw err;
    });
  }

  onQueryChange(e: {filters: FilterWithType[]; sort: SortQuery; pagination: PaginationQuery}): void {
    if(!e.pagination.isPreload) {
      this.loader.loading(true);
    }
    this.getUsers(e.pagination, e.filters, e.sort).then((res) => {
      if(this.tableDataTransformer) {
        this.smartTable.updateTableData(res, e.pagination.isPreload, e.pagination.offset);
      }
      this.loader.loading(false);
    }).catch((err) => {
      this.loader.loading(false, {type: 'error', message: err});
    });
  }

  onLoadingStateChange(state: {navigateToPreloadingPage: boolean, dataReady: boolean}): void {
    if(state.navigateToPreloadingPage) this.loader.loading(true);
    if(state.dataReady) this.loader.loading(false);
  }

  navigateTo(id: any): void {
    this.nav.to(`user/${id}`);
  }
}
